import * as React from "react";
import {t} from "i18next";
import {Table, Label} from "semantic-ui-react";

import {OptionBasicDto, OptionBasicPSLDependencyDto} from "src/services/Client";
import {Dialog} from "../../components/Dialogs/Dialog";
import {Button} from "../../components/Button";

interface IBaseOptionsDeleteConfirmDialogProps {
    canDeleteDtos: OptionBasicDto[];
    cannotDeleteDtos: OptionBasicDto[];
    deleteDependencies: {[key: number]: OptionBasicPSLDependencyDto};
    failedTodelete: number;
    onConfirm(): void;
    onCancel(): void;
}

const emptyContent = (): JSX.Element => {
    return (
        <>
            <Table.Cell><b>{'-'}</b></Table.Cell>
            <Table.Cell><b>{'-'}</b></Table.Cell>
        </>
    );
};

const dependencyContent = (key: string, value: number, withRow: boolean): JSX.Element => {
    const content = (
        <>
            <Table.Cell>
                {key.split('_')[0]} <b>{' -> '}</b> {key.split('_')[1]}
            </Table.Cell>
            <Table.Cell>
                {value}
            </Table.Cell>
        </>
    );

    if (withRow) {
        return (
            <Table.Row>
                {content}
            </Table.Row>
        );
    } else {
        return content;
    }
};

export const BaseOptionsDeleteConfirmDialog: React.StatelessComponent<IBaseOptionsDeleteConfirmDialogProps> = (props: IBaseOptionsDeleteConfirmDialogProps) => {

    return (
        <Dialog
            title={t("BASEOPTIONS:DELETE_CONFIRM_DIALOG")}
            customStyleClassName={"scrollableYDialog"}
            open={true}
            actions={
                [
                    props.canDeleteDtos.length > 0 ?
                        <Button
                            key={"submit"}
                            icon={"checkmark"}
                            label={t("GLOBAL:CONFIRM")}
                            id="delete-submit-button"
                            onClick={props.onConfirm}
                        /> : <></>,
                    <Button
                        key={"cancel"}
                        icon={"ban"}
                        label={t("GLOBAL:CANCEL")}
                        onClick={props.onCancel}
                    />
                ]
            }
        >
            <p>{t("BASEOPTIONS:CONFIRM_DELETE_TEXT")}</p>
            <p><b>{props.canDeleteDtos.length + props.cannotDeleteDtos.length}</b> {' ' + t("BASEOPTIONS:OPTION_BASIC_SELECTED")}</p>

            {props.canDeleteDtos.length > 0 &&
                <>
                    <p><b>{t("BASEOPTIONS:AVAILABLE_FOR_DELETE")}</b></p>
                    {props.canDeleteDtos.map((dto: OptionBasicDto, index: number) => {
                        return (
                            <Label
                                key={"tag-av" + index}
                                tag={true}
                                color={props.deleteDependencies[dto.objectId] ? "red" : "blue"}
                                style={{"marginRight": "10px", "marginBottom": "5px"}}
                            >
                                {dto.optionCode.concat('-', dto.optionType)}
                            </Label>
                        );
                    })}
                </>
            }

            {props.cannotDeleteDtos.length > 0 &&
                <>
                    <p style={{"marginTop": "10px"}}><b>{t("BASEOPTIONS:UNAVAILABLE_FOR_DELETE")}</b></p>
                    {props.cannotDeleteDtos.map((dto: OptionBasicDto, index: number) => {
                        return (
                            <Label
                                key={"tag-unav-" + index}
                                tag={true}
                                style={{"marginRight": "10px", "marginBottom": "5px"}}
                            >
                                {dto.optionCode.concat('-', dto.optionType)}
                            </Label>
                        );
                    })}
                </>
            }
            {props.failedTodelete > 0 &&
              <>
               <p style={{"marginTop": "10px","fontWeight":'700',color:'#950014'}}><b>{props.failedTodelete} : {t("BASEOPTIONS:FAILED_FOR_DELETE")} </b></p>
              </>
            }
            {Object.keys(props.deleteDependencies).length > 0 ?
                <>
                    <p style={{"marginTop": "30px"}}>{t("BASEOPTIONS:CONFIRM_DELETE_DEPENDENCY_TEXT")}</p>
                    <Table celled={true} structured={true}>

                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>
                                    {t("COLUMN_FACTORY:OPTIONBASIC")}
                                </Table.HeaderCell>
                                <Table.HeaderCell>
                                    {t("BASEOPTIONS:DEPENDENCY_TYPE")}
                                </Table.HeaderCell>
                                <Table.HeaderCell colSpan={2}>
                                    {t("BASEOPTIONS:PSL_CONNECTIONS")}
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {props.canDeleteDtos.map((dto: OptionBasicDto, index: number) => {
                                const dtoDependencies = props.deleteDependencies[dto.objectId];
                                if (dtoDependencies) {
                                    const constraintKeys = Object.keys(dtoDependencies.constraintPSLs);
                                    const exclusionKeys = Object.keys(dtoDependencies.exclusionPSLs);
                                    const packageContentKeys = dtoDependencies.packageContentPSLs ? Object.keys(dtoDependencies.packageContentPSLs) : [];

                                    return (
                                        <React.Fragment key={"fr-" + index}>
                                            <Table.Row style={{"borderTop": "2px solid darkgrey"}}>
                                                <Table.Cell
                                                    rowSpan={
                                                        (constraintKeys.length > 0 ? constraintKeys.length : 1) +
                                                        (exclusionKeys.length > 0 ? exclusionKeys.length : 1) +
                                                        (packageContentKeys.length > 0 ? packageContentKeys.length : 1)
                                                    }
                                                >
                                                    <Label ribbon={true} color={"red"}>
                                                        {dto.optionCode.concat('-', dto.optionType)}
                                                    </Label>
                                                </Table.Cell>

                                                <Table.Cell rowSpan={constraintKeys.length > 0 ? constraintKeys.length : 1}>
                                                    <b>{t("COLUMN_FACTORY:CONSTRAINT")}</b>
                                                </Table.Cell>

                                                {
                                                    constraintKeys.length === 0 ?
                                                        emptyContent()
                                                        :
                                                        dependencyContent(constraintKeys[0], dtoDependencies.constraintPSLs[constraintKeys[0]], false)
                                                }
                                            </Table.Row>

                                            {
                                                constraintKeys.length > 1 &&
                                                constraintKeys.slice(1).map((key: string) => {
                                                    return dependencyContent(key, dtoDependencies.constraintPSLs[key], true);
                                                })
                                            }

                                            <Table.Row>
                                                <Table.Cell rowSpan={exclusionKeys.length > 0 ? exclusionKeys.length : 1}>
                                                    <b>{t("COLUMN_FACTORY:EXCLUSION")}</b>
                                                </Table.Cell>

                                                {
                                                    exclusionKeys.length === 0 ?
                                                        emptyContent()
                                                        :
                                                        dependencyContent(exclusionKeys[0], dtoDependencies.exclusionPSLs[exclusionKeys[0]], false)

                                                }
                                            </Table.Row>

                                            {
                                                exclusionKeys.length > 1 &&
                                                exclusionKeys.slice(1).map((key: string) => {
                                                    return dependencyContent(key, dtoDependencies.exclusionPSLs[key], true);
                                                })
                                            }

                                            <Table.Row>
                                                <Table.Cell rowSpan={packageContentKeys.length > 0 ? packageContentKeys.length : 1}>
                                                    <b>{t("COLUMN_FACTORY:PACKAGECONTENT")}</b>
                                                </Table.Cell>

                                                {
                                                    packageContentKeys.length === 0 ?
                                                        emptyContent()
                                                        :
                                                        dependencyContent(packageContentKeys[0], dtoDependencies.packageContentPSLs[packageContentKeys[0]], false)

                                                }
                                            </Table.Row>

                                            {
                                                packageContentKeys.length > 1 &&
                                                packageContentKeys.slice(1).map((key: string) => {
                                                    return dependencyContent(key, dtoDependencies.packageContentPSLs[key], true);
                                                })
                                            }
                                        </React.Fragment>
                                    );
                                } else {
                                    return;
                                }
                            })
                            }
                        </Table.Body>
                    </Table>
                </>
                :
                <></>
            }
        </Dialog>
    );
};



// WEBPACK FOOTER //
// ./src/views/BaseOptionsView/BaseOptionsDeleteConfirmDialog.tsx